import React from "react";
import { Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import Modal from "components/ui/modal/Modal";
import toast from "components/ui/toast/Toast";
import { useAdminCreateBuyerList } from "lib/hooks/api/buyer_lists/admin";
import {
  BuyerContextSource,
  useCreateBuyerList,
} from "lib/hooks/api/buyer_lists/useCreateBuyerList";
import { BuyerListSharingTypeEnum, inferEnumType } from "lib/types/graphQLEnums";
import { BuyerListFormValues } from "./utils/types";
import { CreateBuyerListForm } from "./CreateBuyerListForm";

import css from "./CreateBuyerListModal.module.scss";

type UserModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  buyerGuids: string[];
  contextSource: BuyerContextSource;
};

export function UserCreateBuyerListModal(props: UserModalProps) {
  const { isLoading, mutate } = useCreateBuyerList(props.contextSource, {
    onSuccess: () => {
      toast.success("Buyer list created successfully!");
      props.onOpenChange(false);
    },
    onError: () => toast.error("Failed to create buyer list"),
  });

  const onSubmit = (values: BuyerListFormValues) =>
    mutate({
      name: values.name,
      sharingType: values.sharingType as BuyerListSharingTypeEnum,
      buyerIds: values.buyerGuids,
    });

  return <CreateBuyerListModal {...props} onSubmit={onSubmit} isLoading={isLoading} />;
}

type AdminProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userGuid: string;
};

export function AdminCreateBuyerListModal(props: AdminProps) {
  const { isLoading, mutate } = useAdminCreateBuyerList({
    onSuccess: () => {
      toast.success("Buyer list created successfully!");
      props.onOpenChange(false);
    },
    onError() {
      toast.error("Failed to create buyer list");
    },
  });

  return (
    <CreateBuyerListModal
      {...props}
      buyerGuids={[]}
      onSubmit={(values) =>
        mutate({
          name: values.name,
          sharingType: inferEnumType(values.sharingType),
          buyerIds: values.buyerGuids,
          ownerId: props.userGuid,
        })
      }
      isLoading={isLoading}
    />
  );
}

type ModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  buyerGuids: string[];
  onSubmit: (values: BuyerListFormValues) => void;
  isLoading: boolean;
};

function CreateBuyerListModal({ open, onOpenChange, buyerGuids, onSubmit, isLoading }: ModalProps) {
  return (
    <Modal open={open} onOpenChange={onOpenChange} position="top-center">
      <div className={css.modalContainer}>
        <Text h3>Create new buyer list</Text>
        <CreateBuyerListForm
          onSubmit={onSubmit}
          entityIds={buyerGuids}
          footer={
            <div className={css.footer}>
              <Button
                buttonType="secondary"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" loading={isLoading}>
                {isLoading ? "Creating..." : "Create list"}
              </Button>
            </div>
          }
        />
      </div>
    </Modal>
  );
}
